import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const DEFAULT_AUTO_SEARCH_MIN_CHARACTERS = 3;
const DEFAULT_AUTO_SEARCH_DELAY = 300;

/**
 * Syncs header search inputs with the predictive search modal.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} headerSearchInput
 * @typedef {HTMLElement & {
 *   getSearchValue: () => string,
 *   setSearchValue: (value: string, options?: { runSearch?: boolean, focus?: boolean }) => void
 * }} PredictiveSearchElement
 * @typedef {object} OpenSearchModalOptions
 * @property {boolean} [runSearch]
 * @extends {Component<Refs>}
 */
class SearchButton extends Component {
  requiredRefs = ['headerSearchInput'];

  #controller = new AbortController();
  #debouncedAutoSearch = () => {};

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    this.#debouncedAutoSearch = debounce(this.#openSearchModalFromTyping, this.autoSearchDelay);

    this.#predictiveSearch?.addEventListener('predictive-search:value-change', this.#handlePredictiveSearchValueChange, {
      signal,
    });
    this.#dialog?.addEventListener('dialog:close', this.#syncFromModal, { signal });

    this.#syncFromModal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  handleHeaderInput() {
    this.#syncAllHeaderInputs(this.refs.headerSearchInput.value);
    this.#debouncedAutoSearch();
  }

  /**
   * @param {KeyboardEvent} event
   */
  onHeaderInputKeyDown(event) {
    if (!(event instanceof KeyboardEvent) || event.key !== 'Enter') return;

    event.preventDefault();
    this.openSearchModal();
  }

  /**
   * @param {OpenSearchModalOptions} [options={}]
   */
  openSearchModal(options = {}) {
    const dialog = this.#dialog;
    const predictiveSearch = this.#predictiveSearch;

    if (!dialog || !predictiveSearch) return;

    const searchValue = this.refs.headerSearchInput.value;
    const { runSearch } = options;
    const shouldRunSearch = typeof runSearch === 'boolean' ? runSearch : searchValue.trim().length > 0;

    dialog.showDialog();

    requestAnimationFrame(() => {
      predictiveSearch.setSearchValue(searchValue, {
        runSearch: shouldRunSearch,
        focus: true,
      });
    });
  }

  get autoSearchMinCharacters() {
    return parseIntegerWithFallback(this.dataset.autoSearchMinCharacters, DEFAULT_AUTO_SEARCH_MIN_CHARACTERS);
  }

  get autoSearchDelay() {
    return parseIntegerWithFallback(this.dataset.autoSearchDelay, DEFAULT_AUTO_SEARCH_DELAY);
  }

  /**
   * @returns {import('./dialog').DialogComponent | null}
   */
  get #dialog() {
    const dialog = document.getElementById('search-modal');
    return dialog instanceof HTMLElement ? /** @type {import('./dialog').DialogComponent} */ (dialog) : null;
  }

  /**
   * @returns {PredictiveSearchElement | null}
   */
  get #predictiveSearch() {
    const predictiveSearch = this.#dialog?.querySelector('predictive-search-component');
    return predictiveSearch instanceof HTMLElement ? /** @type {PredictiveSearchElement} */ (predictiveSearch) : null;
  }

  #handlePredictiveSearchValueChange = (/** @type {CustomEvent<{ value?: string }>} */ event) => {
    if (!(event instanceof CustomEvent)) return;

    this.#syncAllHeaderInputs(event.detail?.value ?? '');
  };

  #openSearchModalFromTyping = () => {
    const searchValue = this.refs.headerSearchInput.value.trim();

    if (!searchValue.length || searchValue.length < this.autoSearchMinCharacters) return;

    this.openSearchModal({ runSearch: true });
  };

  #syncFromModal = () => {
    const predictiveSearch = this.#predictiveSearch;
    if (!predictiveSearch || typeof predictiveSearch.getSearchValue !== 'function') return;

    this.#syncAllHeaderInputs(predictiveSearch.getSearchValue());
  };

  /**
   * @param {string} value
   */
  #syncAllHeaderInputs(value) {
    for (const input of document.querySelectorAll('search-button [data-header-search-input]')) {
      if (input instanceof HTMLInputElement) {
        input.value = value;
      }
    }
  }
}

if (!customElements.get('search-button')) {
  customElements.define('search-button', SearchButton);
}

/**
 * @param {string | undefined} value
 * @param {number} fallback
 * @returns {number}
 */
function parseIntegerWithFallback(value, fallback) {
  const parsedValue = Number.parseInt(value ?? '', 10);

  return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : fallback;
}
